import { Pressable, View } from "react-native"
import { Show, useObservable } from "@legendapp/state/react"
import { SafeAreaView } from "react-native-safe-area-context"

import { Text, MCIcon, Header, List, ActivityIndicator } from "@src/components"
import { Alert } from "@src/components/ui/confirm"
import { ExpenseCategoryFormModal, ExpenseModalState } from "@src/components/expense-category-form-modal"
import { trpc, sharedStyles } from "@src/utils"
import { ar } from "@src/i18n"

export default function ExpenseCategoriesScreen() {
  const modal$ = useObservable<ExpenseModalState | undefined>(undefined)
  const utils = trpc.useUtils()
  const deleteCategory = trpc.expenseCategory.delete.useMutation()
  const categories = trpc.expenseCategory.list.useQuery()

  function handleDelete(id: number) {
    Alert.alert(ar.delete, ar.deleteExpenseCategoryConfirmation, [
      { text: ar.cancel, style: "cancel" },
      {
        text: ar.delete,
        style: "destructive",
        onPress: async () => {
          await deleteCategory.mutateAsync(
            { id },
            {
              onSuccess: () => {
                utils.expenseCategory.list.invalidate()
                utils.expense.list.invalidate()
              },
              onError: (e) => Alert.alert(ar.error, e.message),
            },
          )
        },
      },
    ])
  }

  const headerRight = (
    <Pressable onPress={() => modal$.set({ type: "add" })} className="justify-center px-2 h-full active:opacity-70">
      <MCIcon name="plus" size={25} color="blue" />
    </Pressable>
  )

  if (categories.isLoading) {
    return (
      <>
        <Header title={ar.expenseCategories} right={headerRight} />
        <ActivityIndicator className="mt-2" />
      </>
    )
  }

  return (
    <>
      <Header title={ar.expenseCategories} right={headerRight} />

      <List
        contentContainerStyle={sharedStyles.contentContainer}
        data={categories.data?.items ?? []}
        keyExtractor={(item) => item.id.toString()}
        onRefresh={categories.refetch}
        ListEmptyComponent={<Text className="text-gray-500 text-center py-8">{ar.noCategories}</Text>}
        renderItem={({ item }) => (
          <View className="bg-white rounded-xl border border-gray-100 p-3.5 mb-2 shadow-sm flex-row items-center justify-between">
            <View className="flex-row items-center gap-2 flex-1">
              <View className="w-3 h-3 rounded-full bg-gray-400" style={item.color ? { backgroundColor: item.color } : undefined} />
              <Text className="text-base text-gray-900">{item.name}</Text>
            </View>
            <View className="flex-row gap-1">
              <Pressable onPress={() => modal$.set({ type: "edit", id: item.id })} className="p-2">
                <MCIcon name="pencil" size={18} color="#3B82F6" />
              </Pressable>
              <Pressable onPress={() => handleDelete(item.id)} className="p-2">
                <MCIcon name="delete" size={18} color="#EF4444" />
              </Pressable>
            </View>
          </View>
        )}
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
      />

      <Show if={modal$}>
        {(value) => {
          return <ExpenseCategoryFormModal state={value as ExpenseModalState} onClose={() => modal$.set(undefined)} />
        }}
      </Show>
    </>
  )
}
